import React, { useState, useRef, useCallback } from 'react';
import {
  ButtonProps as MuiButtonProps,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItemButton,
  ListItem,
} from '@mui/material';
import { Link } from 'react-router';
import { Typography } from '../../Typography';
import { Button } from '../Button';

export interface DropdownOption {
  /** Label text for the option */
  label?: string;
  /** Optional icon to display before the label */
  startIcon?: React.ReactNode;
  /** Optional icon to display after the label */
  endIcon?: React.ReactNode;
  /** Function to call when option is clicked */
  onClick?: () => void;
  /** URL to navigate to when option is clicked */
  href?: string;
  /** Whether the option is disabled */
  disabled?: boolean;
  /** Whether this option is a divider */
  divider?: boolean;
  /** Optional test ID for the option */
  dataTestId?: string;
}

export interface DropdownButtonProps extends Omit<MuiButtonProps, 'onClick'> {
  /** Button text */
  label: string;
  /** Array of dropdown options */
  options: DropdownOption[];
  /** Optional icon to display before the button label */
  startIcon?: React.ReactNode;
  /** Optional icon to display after the button label */
  endIcon?: React.ReactNode;
  /** Whether the dropdown is currently open */
  open?: boolean;
  /** Callback when dropdown opens */
  onOpen?: () => void;
  /** Callback when dropdown closes */
  onClose?: () => void;

  /** Optional test ID for the button */
  dataTestId?: string;
  /** Whether to use mobile drawer on small devices */
  useMobileDrawer?: boolean;
  /** Menu alignment relative to the button - 'left' or 'right' */
  menuAlignment?: 'left' | 'right';
}

/**
 * A button component that opens a dropdown menu with selectable options.
 * Supports keyboard navigation, accessibility, and flexible content.
 */
export const DropdownButton: React.FC<DropdownButtonProps> = ({
  label,
  options,
  startIcon,
  endIcon,
  open: controlledOpen,
  onOpen,
  onClose,

  dataTestId = 'dropdown-button',
  useMobileDrawer = true,
  disabled = false,
  menuAlignment = 'right',
  ...buttonProps
}) => {
  const theme = useTheme();
  const isSmallDevice = useMediaQuery(theme.breakpoints.down('sm'));
  const [internalOpen, setInternalOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  // Use controlled or uncontrolled open state
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (!disabled) {
        setInternalOpen(true);
        onOpen?.();
      }
    },
    [disabled, onOpen],
  );

  const handleClose = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (event?: object, reason?: 'backdropClick' | 'escapeKeyDown') => {
      if (event && typeof event === 'object' && 'stopPropagation' in event) {
        (event as React.SyntheticEvent).stopPropagation();
      }
      setInternalOpen(false);
      onClose?.();
    },
    [onClose],
  );

  const handleOptionClick = useCallback(
    (option: DropdownOption) => {
      if (option.disabled) return;

      if (option.onClick) {
        option.onClick();
      }

      handleClose();
    },
    [handleClose],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (!isOpen) {
          handleOpen(event as any);
        }
      } else if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        handleClose();
      }
    },
    [isOpen, handleOpen, handleClose],
  );

  const renderMenuItem = useCallback(
    (option: DropdownOption, index: number) => {
      if (option.divider) {
        return <Divider key={`divider-${index}`} />;
      }

      const menuItemContent = (
        <>
          {option.startIcon && <ListItemIcon sx={{ minWidth: 36 }}>{option.startIcon}</ListItemIcon>}
          <ListItemText>
            {option.label && (
              <Typography sx={{ fontSize: '14px', fontWeight: 600, lineHeight: '20px', color: '#252B37' }}>
                {option.label}
              </Typography>
            )}
          </ListItemText>
          {option.endIcon && <Box sx={{ ml: 1 }}>{option.endIcon}</Box>}
        </>
      );

      const commonProps = {
        key: `option-${index}`,
        disabled: option.disabled,
        onClick: () => handleOptionClick(option),
        'data-testid': option.dataTestId || `dropdown-option-${index}`,
        sx: {
          display: 'flex',
          alignItems: 'center',
          m: '6px',
          borderRadius: '8px',
          gap: 1,
          '&:focus-visible': {
            boxShadow: `0px 0px 0px 2px ${theme.palette.primary.main}`,
            borderRadius: '8px',
            backgroundColor: theme.custom?.colours?.grey50 || '#FAFAFA',
          },
          '&:hover': {
            backgroundColor: theme.custom?.colours?.grey50 || '#FAFAFA',
          },
          padding: '10px',
        },
      };

      if (option.href) {
        return (
          <MenuItem {...commonProps} component={Link} to={option.href}>
            {menuItemContent}
          </MenuItem>
        );
      }

      return <MenuItem {...commonProps}>{menuItemContent}</MenuItem>;
    },
    [handleOptionClick],
  );

  const renderMobileMenuItem = useCallback(
    (option: DropdownOption, index: number) => {
      if (option.divider) {
        return <Divider key={`mobile-divider-${index}`} />;
      }

      const listItemContent = (
        <>
          {option.startIcon && <ListItemIcon sx={{ minWidth: 36, color: '#A4A7AE' }}>{option.startIcon}</ListItemIcon>}
          <ListItemText>
            <Typography
              sx={{
                fontSize: '16px',
                color: '#344054',
                fontWeight: 500,
                lineHeight: '20px',
              }}
            >
              {option.label || ''}
            </Typography>
          </ListItemText>
          {option.endIcon && <Box sx={{ ml: 1 }}>{option.endIcon}</Box>}
        </>
      );

      const commonProps = {
        key: `mobile-option-${index}`,
        disabled: option.disabled,
        onClick: () => handleOptionClick(option),
        'data-testid': option.dataTestId || `mobile-dropdown-option-${index}`,
        sx: {
          display: 'flex',
          alignItems: 'center',
          marginBottom: 2,
          borderRadius: '8px',
          gap: 1,
          padding: '10px',
        },
      };

      if (option.href) {
        return (
          <ListItem disablePadding key={`mobile-option-${index}`}>
            <ListItemButton {...commonProps} component={Link} to={option.href}>
              {listItemContent}
            </ListItemButton>
          </ListItem>
        );
      }

      return (
        <ListItem disablePadding key={`mobile-option-${index}`}>
          <ListItemButton {...commonProps}>{listItemContent}</ListItemButton>
        </ListItem>
      );
    },
    [handleOptionClick],
  );

  // Mobile drawer version
  if (isSmallDevice && useMobileDrawer) {
    return (
      <div data-testid={dataTestId}>
        <Button
          ref={anchorRef}
          onClick={handleOpen}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          startIcon={startIcon}
          endIcon={endIcon}
          aria-expanded={isOpen}
          aria-haspopup="true"
          aria-label={`${label} dropdown menu`}
          data-testid={`${dataTestId}-trigger`}
          {...buttonProps}
        >
          {label}
        </Button>
        <Drawer
          anchor="bottom"
          open={isOpen}
          onClose={handleClose}
          data-testid={`${dataTestId}-mobile-menu`}
          slotProps={{
            paper: {
              sx: {
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                maxHeight: '80vh',
              },
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="h3" sx={{ mb: 2, textAlign: 'center' }}>
              {label}
            </Typography>
            <List>{options.map(renderMobileMenuItem)}</List>
          </Box>
        </Drawer>
      </div>
    );
  }

  // Desktop menu version
  return (
    <div data-testid={dataTestId}>
      <Button
        ref={anchorRef}
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        startIcon={startIcon}
        endIcon={endIcon}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={`${label} dropdown button`}
        data-testid={`${dataTestId}-trigger`}
        {...buttonProps}
      >
        {label}
      </Button>
      <Menu
        id="dropdown-menu"
        open={isOpen}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: menuAlignment,
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: menuAlignment,
        }}
        disableScrollLock
        data-testid={`${dataTestId}-menu`}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '8px',
            boxShadow: '0 12px 16px -4px rgba(16, 24, 40, 0.08), 0 4px 6px -2px rgba(16, 24, 40, 0.03);',
            border: '1px solid #EAECF0',
          },
        }}
        slotProps={{
          list: {
            'aria-labelledby': `${dataTestId}-trigger`,
            role: 'menu',
            sx: {
              p: 0,
              minWidth: '228px',
              borderRadius: '8px',
            },
          },
        }}
      >
        {options.map(renderMenuItem)}
      </Menu>
    </div>
  );
};
